import { useEffect, useCallback, useMemo, useState, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Skeleton,
  TextField,
  Autocomplete,
  Chip,
  Tooltip as MuiTooltip,
  Button,
  ButtonGroup,
  LinearProgress,
  ToggleButton,
  ToggleButtonGroup,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tabs,
  Tab,
  Fade,
  Alert,
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import InsightsIcon from '@mui/icons-material/Insights';
import OrganizationsView from '../components/metrics/OrganizationsView';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import BarChartIcon from '@mui/icons-material/BarChart';
import NumbersIcon from '@mui/icons-material/Numbers';
import PercentIcon from '@mui/icons-material/Percent';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import SummarizeIcon from '@mui/icons-material/Summarize';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import GenerateReportDialog from '../components/GenerateReportDialog';
import GenerateEmailModal from '../components/GenerateEmailModal';
import WeeklyTLDRModal from '../components/WeeklyTLDRModal';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  Legend,
  AreaChart,
  Area,
} from 'recharts';
import { differenceInDays, format, subDays, subMonths } from 'date-fns';
import { useMetricsStore, DauChartView, OrgChartView } from '../stores/metricsStore';
import { useExclusionsStore } from '../stores/exclusionsStore';
import { useFilterStore } from '../stores/filterStore';
import { formatOrgDisplayName } from '../utils/display';
import {
  getActiveUsersSummary,
  getDauTimeseries,
  getDauByOrgTimeseries,
  getWauTimeseries,
  getActivityByOrg,
  getActivityByEventType,
  getUserRetention,
  getHoursSavedSummary,
} from '../api/dashboard';
import type { FilterOption, OrgDailyUsers, HoursSavedSummary } from '../types';

interface QuickRange {
  label: string;
  days?: number;
  months?: number;
}

const QUICK_RANGES: QuickRange[] = [
  { label: '7D', days: 7 },
  { label: '30D', days: 30 },
  { label: '90D', days: 90 },
  { label: '6M', months: 6 },
  { label: '1Y', months: 12 },
];

// Color palette for organizations in stacked bar chart
const ORG_COLORS = [
  '#10b981', '#8b5cf6', '#f59e0b', '#ef4444', '#06b6d4',
  '#ec4899', '#84cc16', '#f97316', '#6366f1', '#14b8a6',
  '#a855f7', '#eab308', '#22c55e', '#3b82f6', '#f43f5e',
  '#0ea5e9', '#d946ef', '#facc15', '#4ade80', '#818cf8',
];

export default function MetricsDashboard() {
  const {
    summary,
    dauTimeseries,
    dauByOrgTimeseries,
    wauTimeseries,
    activityByOrg,
    activityByEventType,
    retention,
    isLoading,
    dateRange,
    selectedOrgIds,
    availableOrgs,
    dauChartView,
    orgChartView,
    selectedTab,
    setSummary,
    setDauTimeseries,
    setDauByOrgTimeseries,
    setWauTimeseries,
    setActivityByOrg,
    setActivityByEventType,
    setRetention,
    setLoading,
    setDateRange,
    setSelectedOrgIds,
    setAvailableOrgs,
    setDauChartView,
    setOrgChartView,
    setSelectedTab,
  } = useMetricsStore();

  const { excludedOrgIds, effectiveExcludedUserIds } = useExclusionsStore();
  const { availableOptions, isOptionsLoaded } = useFilterStore();

  // Track initialization to prevent cascading refetches
  const isInitializedRef = useRef(false);
  const isFetchingRef = useRef(false);

  const handleQuickRange = (range: QuickRange) => {
    const now = new Date();
    const end = new Date(now);
    end.setHours(23, 59, 59, 999);

    let start: Date;
    if (range.months) {
      start = subMonths(end, range.months);
    } else if (range.days) {
      start = subDays(end, range.days);
    } else {
      start = subDays(end, 30);
    }
    start.setHours(0, 0, 0, 0);

    setDateRange(start, end);
  };

  const isActiveRange = (range: QuickRange): boolean => {
    const now = new Date();
    const end = new Date(now);
    end.setHours(23, 59, 59, 999);

    let expectedStart: Date;
    if (range.months) {
      expectedStart = subMonths(end, range.months);
    } else if (range.days) {
      expectedStart = subDays(end, range.days);
    } else {
      return false;
    }
    expectedStart.setHours(0, 0, 0, 0);

    const diffDays = Math.abs(differenceInDays(dateRange.start, expectedStart));
    return diffDays < 1;
  };

  const fetchMetrics = useCallback(async () => {
    // Prevent concurrent fetches
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;

    setLoading(true);
    try {
      const orgIds = selectedOrgIds.length > 0 ? selectedOrgIds : undefined;
      const exclOrgs = excludedOrgIds.length > 0 ? excludedOrgIds : undefined;
      const exclUsers = effectiveExcludedUserIds.length > 0 ? effectiveExcludedUserIds : undefined;

      const [summaryData, dauData, dauByOrgData, wauData, orgData, eventData, retentionData] = await Promise.all([
        getActiveUsersSummary(dateRange.start, dateRange.end, orgIds, exclOrgs, exclUsers),
        getDauTimeseries(dateRange.start, dateRange.end, orgIds, exclOrgs, exclUsers),
        getDauByOrgTimeseries(dateRange.start, dateRange.end, orgIds, exclOrgs, exclUsers),
        getWauTimeseries(dateRange.start, dateRange.end, orgIds, exclOrgs, exclUsers),
        getActivityByOrg(dateRange.start, dateRange.end, 100, exclOrgs, exclUsers),
        getActivityByEventType(dateRange.start, dateRange.end, orgIds, exclOrgs, exclUsers),
        getUserRetention(dateRange.start, dateRange.end, orgIds, exclOrgs, exclUsers),
      ]);
      setSummary(summaryData);
      setDauTimeseries(dauData);
      setDauByOrgTimeseries(dauByOrgData);
      setWauTimeseries(wauData);
      setActivityByOrg(orgData);
      setActivityByEventType(eventData);
      setRetention(retentionData);
      setActivityError(null);

      // Fetch hours saved separately (heavy calculation)
      setIsHoursSavedLoading(true);
      getHoursSavedSummary(dateRange.start, dateRange.end, orgIds, exclOrgs, exclUsers)
        .then(setHoursSavedSummary)
        .catch((err) => console.error('Failed to fetch hours saved:', err))
        .finally(() => setIsHoursSavedLoading(false));
    } catch (error) {
      console.error('Failed to fetch metrics:', error);
      setActivityError(
        error instanceof Error
          ? `Couldn't load activity metrics: ${error.message}`
          : "Couldn't load activity metrics.",
      );
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  }, [dateRange, selectedOrgIds, excludedOrgIds, effectiveExcludedUserIds, setSummary, setDauTimeseries, setDauByOrgTimeseries, setWauTimeseries, setActivityByOrg, setActivityByEventType, setRetention, setLoading]);

  // Initial load: use orgs from filterStore (already loaded by AuthGuard) then fetch metrics
  useEffect(() => {
    if (isInitializedRef.current) return;

    const initialize = async () => {
      // Use orgs from filterStore if available (loaded by AuthGuard)
      if (isOptionsLoaded && availableOrgs.length === 0) {
        setAvailableOrgs(availableOptions.orgIds);
      }
      // Only fetch the (heavier) activity metrics if the activity tab is the active one.
      // The Organizations view fetches its own data on mount.
      if (selectedTab === 'activity') {
        await fetchMetrics();
      }
      isInitializedRef.current = true;
    };

    initialize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refetch activity metrics when filters change OR when switching back to the activity tab
  useEffect(() => {
    if (!isInitializedRef.current) return;
    if (selectedTab !== 'activity') return;
    fetchMetrics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange, selectedOrgIds, excludedOrgIds, effectiveExcludedUserIds, selectedTab]);

  const handleStartDateChange = (newValue: Date | null) => {
    if (!newValue) return;
    const newStart = new Date(newValue);
    newStart.setHours(0, 0, 0, 0);
    setDateRange(newStart, dateRange.end);
  };

  const handleEndDateChange = (newValue: Date | null) => {
    if (!newValue) return;
    const newEnd = new Date(newValue);
    newEnd.setHours(23, 59, 59, 999);
    setDateRange(dateRange.start, newEnd);
  };

  const MetricCard = ({
    title,
    value,
    subtitle,
    color,
    tooltip,
    percentage,
    percentageTotal,
    percentageLabel = 'registered users',
    trend,
  }: {
    title: string;
    value: string | number;
    subtitle?: string;
    color?: string;
    tooltip?: string;
    percentage?: number | null;
    percentageTotal?: number;
    percentageLabel?: string;
    trend?: number | null;
  }) => (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography variant="overline" color="text.secondary">
            {title}
          </Typography>
          {tooltip && (
            <MuiTooltip
              title={tooltip}
              arrow
              placement="top"
              slotProps={{
                tooltip: {
                  sx: {
                    bgcolor: '#1e293b',
                    fontSize: '0.75rem',
                    maxWidth: 280,
                    p: 1.5,
                  },
                },
              }}
            >
              <InfoOutlinedIcon
                sx={{
                  fontSize: 14,
                  color: 'text.secondary',
                  cursor: 'help',
                  '&:hover': { color: 'primary.main' },
                }}
              />
            </MuiTooltip>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Typography variant="h4" fontWeight={600} sx={{ color: color || 'inherit' }}>
            {value}
          </Typography>
          {percentage != null && percentage > 0 && (
            <MuiTooltip
              title={`${value} out of ${percentageTotal?.toLocaleString() || 0} ${percentageLabel}`}
              arrow
              placement="top"
              slotProps={{
                tooltip: {
                  sx: {
                    bgcolor: '#1e293b',
                    fontSize: '0.75rem',
                    p: 1,
                  },
                },
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ cursor: 'help', '&:hover': { color: 'primary.main' } }}
              >
                {percentage.toFixed(0)}%
              </Typography>
            </MuiTooltip>
          )}
          {trend != null && trend !== 0 && (
            <MuiTooltip
              title={`${trend > 0 ? '+' : ''}${trend.toFixed(1)}% vs previous period`}
              arrow
              placement="top"
              slotProps={{
                tooltip: {
                  sx: {
                    bgcolor: '#1e293b',
                    fontSize: '0.75rem',
                    p: 1,
                  },
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'help' }}>
                {trend > 0 ? (
                  <TrendingUpIcon sx={{ fontSize: 18, color: '#10b981' }} />
                ) : (
                  <TrendingDownIcon sx={{ fontSize: 18, color: '#ef4444' }} />
                )}
                <Typography
                  variant="caption"
                  sx={{ color: trend > 0 ? '#10b981' : '#ef4444', fontWeight: 600, ml: 0.25 }}
                >
                  {Math.abs(trend).toFixed(0)}%
                </Typography>
              </Box>
            </MuiTooltip>
          )}
        </Box>
        {subtitle && (
          <Typography variant="caption" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  );

  // Prepare DAU chart data with formatted dates
  const dauChartData = useMemo(() => dauTimeseries.map((point) => ({
    ...point,
    dateLabel: format(new Date(point.date), 'MMM d'),
  })), [dauTimeseries]);

  // Prepare DAU by Org stacked bar chart data
  // Transform data into format suitable for Recharts stacked bar chart
  const { stackedBarData, allOrgNames, orgColorMap, orgUserDataMap } = useMemo(() => {
    // Get all unique org names across all days, sorted by total users
    const orgTotals = new Map<string, number>();
    dauByOrgTimeseries.forEach((day) => {
      day.orgs.forEach((org) => {
        orgTotals.set(org.orgName, (orgTotals.get(org.orgName) || 0) + org.userCount);
      });
    });

    const sortedOrgNames = Array.from(orgTotals.entries())
      .filter(([, total]) => total > 0) // Only include orgs with logged-in users
      .sort((a, b) => b[1] - a[1])
      .map(([name]) => name);

    // Create color map for orgs
    const colorMap: Record<string, string> = {};
    sortedOrgNames.forEach((name, idx) => {
      colorMap[name] = ORG_COLORS[idx % ORG_COLORS.length];
    });

    // Create data map for user names (for tooltips)
    const userDataMap: Record<string, Record<string, OrgDailyUsers>> = {};

    // Transform data for stacked bar chart
    const data = dauByOrgTimeseries.map((day) => {
      const dayKey = day.date;
      userDataMap[dayKey] = {};

      const dataPoint: Record<string, string | number> = {
        date: day.date,
        dateLabel: format(new Date(day.date), 'MMM d'),
        totalUsers: day.totalUsers,
      };

      // Add user count for each org
      day.orgs.forEach((org) => {
        dataPoint[org.orgName] = org.userCount;
        userDataMap[dayKey][org.orgName] = org;
      });

      // Fill in zeros for orgs not present on this day
      sortedOrgNames.forEach((name) => {
        if (!(name in dataPoint)) {
          dataPoint[name] = 0;
        }
      });

      return dataPoint;
    });

    return {
      stackedBarData: data,
      allOrgNames: sortedOrgNames,
      orgColorMap: colorMap,
      orgUserDataMap: userDataMap,
    };
  }, [dauByOrgTimeseries]);

  // Prepare WAU chart data with formatted dates
  const wauChartData = useMemo(() => wauTimeseries.map((point) => ({
    ...point,
    dateLabel: format(new Date(point.date), 'MMM d'),
  })), [wauTimeseries]);

  // Compute active vs inactive orgs for the Orgs stat card
  const { activeOrgsList, inactiveOrgsList } = useMemo(() => {
    // Create set of excluded org IDs for fast lookup
    const excludedSet = new Set(excludedOrgIds);

    // Get org IDs that are active in the current period from activityByOrg
    const activeOrgIds = new Set(activityByOrg.map((org) => org.orgId));

    // Active orgs - from activityByOrg, excluding globally excluded orgs
    const active = activityByOrg
      .filter((org) => !excludedSet.has(org.orgId))
      .map((org) => ({
        id: org.orgId,
        name: formatOrgDisplayName(org.orgName),
      }))
      .sort((a, b) => a.name.localeCompare(b.name));

    // Inactive orgs - from availableOrgs but not in activityByOrg, excluding globally excluded orgs
    const inactive = availableOrgs
      .filter((org) => !activeOrgIds.has(org.id) && !excludedSet.has(org.id))
      .map((org) => ({
        id: org.id,
        name: formatOrgDisplayName(org.name),
      }))
      .sort((a, b) => a.name.localeCompare(b.name));

    return { activeOrgsList: active, inactiveOrgsList: inactive };
  }, [activityByOrg, availableOrgs, excludedOrgIds]);

  // Top event categories for the chart
  const topEventCategories = useMemo(() => activityByEventType
    .reduce((acc, item) => {
      const existing = acc.find(x => x.category === item.eventCategory);
      if (existing) {
        existing.count += item.count;
      } else {
        acc.push({ category: item.eventCategory, count: item.count });
      }
      return acc;
    }, [] as { category: string; count: number }[])
    .sort((a, b) => b.count - a.count)
    .slice(0, 8), [activityByEventType]);

  const selectedOrgs = availableOrgs.filter((o) => selectedOrgIds.includes(o.id));

  // State for selected bar popup (custom implementation for full control)
  const [selectedBarDate, setSelectedBarDate] = useState<string | null>(null);

  // State for report generation dialog
  const [reportDialogOpen, setReportDialogOpen] = useState(false);

  // State for email generation modal
  const [emailModalOpen, setEmailModalOpen] = useState(false);

  // State for weekly TLDR modal
  const [tldrModalOpen, setTldrModalOpen] = useState(false);

  // State for generate menu
  const [generateMenuAnchor, setGenerateMenuAnchor] = useState<null | HTMLElement>(null);

  // State for hours saved summary (fetched separately due to heavy calculation)
  const [hoursSavedSummary, setHoursSavedSummary] = useState<HoursSavedSummary | null>(null);
  const [isHoursSavedLoading, setIsHoursSavedLoading] = useState(false);
  const [activityError, setActivityError] = useState<string | null>(null);

  // Get data for the selected bar popup
  const selectedBarData = useMemo(() => {
    if (!selectedBarDate) return null;
    const dateData = stackedBarData.find((d) => d.date === selectedBarDate);
    if (!dateData) return null;
    const dayOrgData = orgUserDataMap[selectedBarDate];
    if (!dayOrgData) return null;

    // Get orgs with users, sorted by count
    const orgsWithUsers = allOrgNames
      .map((orgName) => ({
        orgName,
        userCount: (dateData[orgName] as number) || 0,
        userNames: dayOrgData[orgName]?.userNames || [],
        color: orgColorMap[orgName],
      }))
      .filter((org) => org.userCount > 0)
      .sort((a, b) => b.userCount - a.userCount);

    return {
      date: selectedBarDate,
      totalUsers: dateData.totalUsers as number,
      orgs: orgsWithUsers,
    };
  }, [selectedBarDate, stackedBarData, orgUserDataMap, allOrgNames, orgColorMap]);

  // Handle bar click
  const handleBarClick = useCallback((data: { payload?: Record<string, unknown> }) => {
    const clickedDate = data.payload?.date as string;
    if (clickedDate === selectedBarDate) {
      setSelectedBarDate(null); // Toggle off if clicking same bar
    } else {
      setSelectedBarDate(clickedDate);
    }
  }, [selectedBarDate]);

  // Close popup when clicking outside
  const handleChartClick = useCallback(() => {
    // This is called when clicking the chart area (not on a bar)
    // We don't close here as it conflicts with bar clicks
  }, []);

  // Close popup
  const handleClosePopup = useCallback(() => {
    setSelectedBarDate(null);
  }, []);

  const activityInitialLoading = selectedTab === 'activity' && isLoading && !summary;

  return (
    <Box>
      {/* Loading indicator - thin progress bar at the top */}
      {isLoading && !!summary && (
        <LinearProgress
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 1200,
            height: 3,
          }}
        />
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h5" fontWeight={600}>
            Org Metrics
          </Typography>
          <Button
            variant="outlined"
            size="small"
            endIcon={<KeyboardArrowDownIcon />}
            onClick={(e) => setGenerateMenuAnchor(e.currentTarget)}
          >
            Customer Success Agent
          </Button>
          <Menu
            anchorEl={generateMenuAnchor}
            open={Boolean(generateMenuAnchor)}
            onClose={() => setGenerateMenuAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            slotProps={{
              paper: {
                sx: {
                  bgcolor: '#1e293b',
                  border: '1px solid rgba(148, 163, 184, 0.1)',
                  minWidth: 200,
                },
              },
            }}
          >
            <MenuItem
              onClick={() => {
                setGenerateMenuAnchor(null);
                setReportDialogOpen(true);
              }}
              sx={{ py: 1.5 }}
            >
              <ListItemIcon>
                <PictureAsPdfIcon fontSize="small" sx={{ color: '#8b5cf6' }} />
              </ListItemIcon>
              <ListItemText
                primary="Usage Report (PDF)"
                primaryTypographyProps={{ fontSize: '0.875rem' }}
              />
            </MenuItem>
            <MenuItem
              onClick={() => {
                setGenerateMenuAnchor(null);
                setEmailModalOpen(true);
              }}
              sx={{ py: 1.5 }}
            >
              <ListItemIcon>
                <AutoAwesomeIcon fontSize="small" sx={{ color: '#a78bfa' }} />
              </ListItemIcon>
              <ListItemText
                primary="AI Customer Email"
                primaryTypographyProps={{ fontSize: '0.875rem' }}
              />
            </MenuItem>
            <MenuItem
              onClick={() => {
                setGenerateMenuAnchor(null);
                setTldrModalOpen(true);
              }}
              sx={{ py: 1.5 }}
            >
              <ListItemIcon>
                <SummarizeIcon fontSize="small" sx={{ color: '#10b981' }} />
              </ListItemIcon>
              <ListItemText
                primary="Weekly TLDR"
                primaryTypographyProps={{ fontSize: '0.875rem' }}
              />
            </MenuItem>
          </Menu>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Autocomplete
            multiple
            size="small"
            options={availableOrgs}
            getOptionLabel={(option: FilterOption) => formatOrgDisplayName(option.name)}
            value={selectedOrgs}
            onChange={(_, newValue) => setSelectedOrgIds(newValue.map((v) => v.id))}
            renderInput={(params) => (
              <TextField {...params} label="Organizations" placeholder="All" />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={option.id}
                  label={formatOrgDisplayName(option.name)}
                  size="small"
                />
              ))
            }
            sx={{ minWidth: 250 }}
          />

          <ButtonGroup size="small" variant="outlined">
            {QUICK_RANGES.map((range) => (
              <Button
                key={range.label}
                onClick={() => handleQuickRange(range)}
                variant={isActiveRange(range) ? 'contained' : 'outlined'}
                sx={{ minWidth: 48 }}
              >
                {range.label}
              </Button>
            ))}
          </ButtonGroup>

          <DatePicker
            label="Start"
            value={dateRange.start}
            onChange={handleStartDateChange}
            slotProps={{ textField: { size: 'small', sx: { width: 160 } } }}
          />
          <Typography variant="body2" color="text.secondary">to</Typography>
          <DatePicker
            label="End"
            value={dateRange.end}
            onChange={handleEndDateChange}
            slotProps={{ textField: { size: 'small', sx: { width: 160 } } }}
          />
        </Box>
      </Box>

      {/* Tab switcher — both views share the filter bar above */}
      <Box sx={{ borderBottom: '1px solid rgba(148, 163, 184, 0.12)', mb: 3 }}>
        <Tabs
          value={selectedTab}
          onChange={(_, value) => setSelectedTab(value)}
          sx={{
            minHeight: 40,
            '& .MuiTab-root': {
              minHeight: 40,
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '0.875rem',
              gap: 0.75,
              px: 2,
              color: 'text.secondary',
              '&.Mui-selected': { color: 'primary.main', fontWeight: 600 },
            },
            '& .MuiTabs-indicator': {
              backgroundColor: '#8b5cf6',
              height: 2,
            },
          }}
        >
          <Tab
            value="organizations"
            label="Organizations"
            icon={<BusinessIcon sx={{ fontSize: 16 }} />}
            iconPosition="start"
          />
          <Tab
            value="activity"
            label="Activity Trends"
            icon={<InsightsIcon sx={{ fontSize: 16 }} />}
            iconPosition="start"
          />
        </Tabs>
      </Box>

      {selectedTab === 'organizations' && (
        <Fade in timeout={200}>
          <Box>
            <OrganizationsView />
          </Box>
        </Fade>
      )}

      {selectedTab === 'activity' && activityError && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={() => fetchMetrics()}>
              Retry
            </Button>
          }
        >
          {activityError}
        </Alert>
      )}

      {selectedTab === 'activity' && activityInitialLoading && (
        <Box>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {[...Array(6)].map((_, i) => (
              <Grid item xs={6} sm={4} md={2} key={i}>
                <Skeleton variant="rounded" height={110} />
              </Grid>
            ))}
          </Grid>
          <Skeleton variant="rounded" height={400} />
        </Box>
      )}

      {selectedTab === 'activity' && !activityInitialLoading && (
      <Fade in timeout={200}>
      <Box>
      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={6} sm={4} md={2}>
          <MetricCard
            title="Avg DAU"
            value={summary?.dau?.toLocaleString() || 0}
            subtitle="Avg Daily Active Users"
            color="#8b5cf6"
            tooltip="Average Daily Active Users. Counts unique users with activity each weekday, then averages across the selected period. Weekends are excluded."
            percentage={summary?.totalUsers ? (summary.dau / summary.totalUsers) * 100 : null}
            percentageTotal={summary?.totalUsers}
            trend={summary?.dauTrend}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <MetricCard
            title="Avg WAU"
            value={summary?.wau?.toLocaleString() || 0}
            subtitle="Avg Weekly Active Users"
            color="#06b6d4"
            tooltip="Average Weekly Active Users. Counts unique users per work week (Mon-Sun), then averages across all weeks in the period."
            percentage={summary?.totalUsers ? (summary.wau / summary.totalUsers) * 100 : null}
            percentageTotal={summary?.totalUsers}
            trend={summary?.wauTrend}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <MetricCard
            title="Avg MAU"
            value={summary?.mau?.toLocaleString() || 0}
            subtitle="Avg Monthly Active Users"
            color="#10b981"
            tooltip="Average Monthly Active Users. Counts unique users per calendar month, then averages across all months in the period."
            percentage={summary?.totalUsers ? (summary.mau / summary.totalUsers) * 100 : null}
            percentageTotal={summary?.totalUsers}
            trend={summary?.mauTrend}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <MetricCard
            title="Avg DAO"
            value={summary?.dao?.toLocaleString() || 0}
            subtitle="Avg Daily Active Orgs"
            tooltip="Average Daily Active Organizations. Counts unique organizations with activity each weekday, then averages across the selected period. Weekends are excluded."
            percentage={summary?.totalOrgs ? (summary.dao / summary.totalOrgs) * 100 : null}
            percentageTotal={summary?.totalOrgs}
            percentageLabel="active orgs"
            trend={summary?.daoTrend}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <MetricCard
            title="Avg WAO"
            value={summary?.wao?.toLocaleString() || 0}
            subtitle="Avg Weekly Active Orgs"
            tooltip="Average Weekly Active Organizations. Counts unique organizations per work week (Mon-Sun), then averages across all weeks in the period."
            percentage={summary?.totalOrgs ? (summary.wao / summary.totalOrgs) * 100 : null}
            percentageTotal={summary?.totalOrgs}
            percentageLabel="active orgs"
            trend={summary?.waoTrend}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <MetricCard
            title="Avg MAO"
            value={summary?.mao?.toLocaleString() || 0}
            subtitle="Avg Monthly Active Orgs"
            tooltip="Average Monthly Active Organizations. Counts unique organizations per calendar month, then averages across all months in the period."
            percentage={summary?.totalOrgs ? (summary.mao / summary.totalOrgs) * 100 : null}
            percentageTotal={summary?.totalOrgs}
            percentageLabel="active orgs"
            trend={summary?.maoTrend}
          />
        </Grid>
      </Grid>

      {/* Retention & Active Projects Row */}
      <Box sx={{ display: 'flex', gap: 3, mb: 3, flexWrap: 'wrap' }}>
        {/* Retention Card */}
        {retention && (
        <Paper sx={{ p: 3, flex: '1 1 auto' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Typography variant="h6">
              User Retention ({retention.periodDays}-day period)
            </Typography>
            <MuiTooltip
              title="Compares users active in the current period vs the previous period of equal length. Helps understand user engagement trends."
              arrow
              placement="top"
              slotProps={{
                tooltip: {
                  sx: {
                    bgcolor: '#1e293b',
                    fontSize: '0.75rem',
                    maxWidth: 280,
                    p: 1.5,
                  },
                },
              }}
            >
              <InfoOutlinedIcon
                sx={{
                  fontSize: 16,
                  color: 'text.secondary',
                  cursor: 'help',
                  '&:hover': { color: 'primary.main' },
                }}
              />
            </MuiTooltip>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={2}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="overline" color="text.secondary">Previous Period</Typography>
                <MuiTooltip title="Total unique users active during the period immediately before the selected date range (same duration)." arrow placement="top" slotProps={{ tooltip: { sx: { bgcolor: '#1e293b', fontSize: '0.75rem', maxWidth: 280, p: 1.5 } } }}>
                  <InfoOutlinedIcon sx={{ fontSize: 12, color: 'text.secondary', cursor: 'help', '&:hover': { color: 'primary.main' } }} />
                </MuiTooltip>
              </Box>
              <Typography variant="h5" fontWeight={600}>{retention.previousUsers}</Typography>
            </Grid>
            <Grid item xs={6} sm={2}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="overline" color="text.secondary">Current Period</Typography>
                <MuiTooltip title="Total unique users active during the selected date range." arrow placement="top" slotProps={{ tooltip: { sx: { bgcolor: '#1e293b', fontSize: '0.75rem', maxWidth: 280, p: 1.5 } } }}>
                  <InfoOutlinedIcon sx={{ fontSize: 12, color: 'text.secondary', cursor: 'help', '&:hover': { color: 'primary.main' } }} />
                </MuiTooltip>
              </Box>
              <Typography variant="h5" fontWeight={600}>{retention.currentUsers}</Typography>
            </Grid>
            <Grid item xs={6} sm={2}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="overline" color="text.secondary">Retained Users</Typography>
                <MuiTooltip title="Users who were active in both the previous period and the current period." arrow placement="top" slotProps={{ tooltip: { sx: { bgcolor: '#1e293b', fontSize: '0.75rem', maxWidth: 280, p: 1.5 } } }}>
                  <InfoOutlinedIcon sx={{ fontSize: 12, color: 'text.secondary', cursor: 'help', '&:hover': { color: 'primary.main' } }} />
                </MuiTooltip>
              </Box>
              <MuiTooltip
                title={
                  retention.retainedUserNames?.length > 0
                    ? retention.retainedUserNames.join('\n')
                    : 'No users'
                }
                arrow
                placement="bottom"
                slotProps={{
                  tooltip: {
                    sx: {
                      bgcolor: '#1e293b',
                      fontSize: '0.75rem',
                      maxWidth: 320,
                      p: 1.5,
                      whiteSpace: 'pre-line',
                      lineHeight: 1.6,
                    },
                  },
                }}
              >
                <Box sx={{ display: 'inline-flex', alignItems: 'baseline', gap: 0.75, cursor: 'help' }}>
                  <Typography variant="h5" fontWeight={600}>
                    {retention.retainedUsers}
                  </Typography>
                  {retention.currentUsers > 0 && (
                    <Typography variant="caption" color="text.secondary">
                      {((retention.retainedUsers / retention.currentUsers) * 100).toFixed(0)}%
                    </Typography>
                  )}
                </Box>
              </MuiTooltip>
            </Grid>
            <Grid item xs={6} sm={2}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="overline" color="text.secondary">New Users</Typography>
                <MuiTooltip title="Users active in the current period who were NOT active in the previous period." arrow placement="top" slotProps={{ tooltip: { sx: { bgcolor: '#1e293b', fontSize: '0.75rem', maxWidth: 280, p: 1.5 } } }}>
                  <InfoOutlinedIcon sx={{ fontSize: 12, color: 'text.secondary', cursor: 'help', '&:hover': { color: 'primary.main' } }} />
                </MuiTooltip>
              </Box>
              <MuiTooltip
                title={
                  retention.newUserNames?.length > 0
                    ? retention.newUserNames.join('\n')
                    : 'No users'
                }
                arrow
                placement="bottom"
                slotProps={{
                  tooltip: {
                    sx: {
                      bgcolor: '#1e293b',
                      fontSize: '0.75rem',
                      maxWidth: 320,
                      p: 1.5,
                      whiteSpace: 'pre-line',
                      lineHeight: 1.6,
                    },
                  },
                }}
              >
                <Box sx={{ display: 'inline-flex', alignItems: 'baseline', gap: 0.75, cursor: 'help' }}>
                  <Typography variant="h5" fontWeight={600} color="#06b6d4">
                    {retention.newUsers}
                  </Typography>
                  {retention.currentUsers > 0 && (
                    <Typography variant="caption" color="text.secondary">
                      {((retention.newUsers / retention.currentUsers) * 100).toFixed(0)}%
                    </Typography>
                  )}
                </Box>
              </MuiTooltip>
            </Grid>
            <Grid item xs={6} sm={2}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="overline" color="text.secondary">Absent Users</Typography>
                <MuiTooltip title="Users who were active in the previous period but NOT active in the current period." arrow placement="top" slotProps={{ tooltip: { sx: { bgcolor: '#1e293b', fontSize: '0.75rem', maxWidth: 280, p: 1.5 } } }}>
                  <InfoOutlinedIcon sx={{ fontSize: 12, color: 'text.secondary', cursor: 'help', '&:hover': { color: 'primary.main' } }} />
                </MuiTooltip>
              </Box>
              <MuiTooltip
                title={
                  retention.churnedUserNames?.length > 0
                    ? retention.churnedUserNames.join('\n')
                    : 'No users'
                }
                arrow
                placement="bottom"
                slotProps={{
                  tooltip: {
                    sx: {
                      bgcolor: '#1e293b',
                      fontSize: '0.75rem',
                      maxWidth: 320,
                      p: 1.5,
                      whiteSpace: 'pre-line',
                      lineHeight: 1.6,
                    },
                  },
                }}
              >
                <Box sx={{ display: 'inline-flex', alignItems: 'baseline', gap: 0.75, cursor: 'help' }}>
                  <Typography variant="h5" fontWeight={600} color="#ef4444">
                    {retention.churnedUsers}
                  </Typography>
                  {retention.previousUsers > 0 && (
                    <Typography variant="caption" color="text.secondary">
                      {((retention.churnedUsers / retention.previousUsers) * 100).toFixed(0)}%
                    </Typography>
                  )}
                </Box>
              </MuiTooltip>
            </Grid>
            <Grid item xs={6} sm={2}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="overline" color="text.secondary">Retention Rate</Typography>
                <MuiTooltip title="Percentage of users from the previous period who remained active in the current period. Formula: (Retained Users / Previous Period Users) × 100" arrow placement="top" slotProps={{ tooltip: { sx: { bgcolor: '#1e293b', fontSize: '0.75rem', maxWidth: 280, p: 1.5 } } }}>
                  <InfoOutlinedIcon sx={{ fontSize: 12, color: 'text.secondary', cursor: 'help', '&:hover': { color: 'primary.main' } }} />
                </MuiTooltip>
              </Box>
              <Typography variant="h5" fontWeight={600} color="#10b981">
                {retention.retentionRate.toFixed(1)}%
              </Typography>
            </Grid>
          </Grid>
        </Paper>
        )}

        {/* Active Orgs Card */}
        <MuiTooltip
          title={
            <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
              {activeOrgsList.length > 0 && (
                <Box sx={{ mb: inactiveOrgsList.length > 0 ? 1.5 : 0 }}>
                  <Typography variant="caption" sx={{ color: '#10b981', fontWeight: 600, display: 'block', mb: 0.5 }}>
                    Active ({activeOrgsList.length})
                  </Typography>
                  {activeOrgsList.map((org) => (
                    <Typography key={org.id} variant="caption" sx={{ display: 'block', color: '#d1fae5', lineHeight: 1.4 }}>
                      • {org.name}
                    </Typography>
                  ))}
                </Box>
              )}
              {inactiveOrgsList.length > 0 && (
                <Box>
                  <Typography variant="caption" sx={{ color: '#ef4444', fontWeight: 600, display: 'block', mb: 0.5 }}>
                    Inactive ({inactiveOrgsList.length})
                  </Typography>
                  {inactiveOrgsList.map((org) => (
                    <Typography key={org.id} variant="caption" sx={{ display: 'block', color: '#fecaca', lineHeight: 1.4 }}>
                      • {org.name}
                    </Typography>
                  ))}
                </Box>
              )}
              {activeOrgsList.length === 0 && inactiveOrgsList.length === 0 && (
                <Typography variant="caption" color="text.secondary">No organizations</Typography>
              )}
            </Box>
          }
          arrow
          placement="bottom"
          slotProps={{
            tooltip: {
              sx: {
                bgcolor: '#1e293b',
                fontSize: '0.75rem',
                maxWidth: 320,
                p: 1.5,
              },
            },
          }}
        >
          <Paper sx={{ p: 2, minWidth: 110, display: 'flex', flexDirection: 'column', justifyContent: 'center', cursor: 'help' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography variant="overline" color="text.secondary">
                Active Orgs
              </Typography>
              <InfoOutlinedIcon
                sx={{
                  fontSize: 14,
                  color: 'text.secondary',
                  '&:hover': { color: 'primary.main' },
                }}
              />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
              <Typography variant="h4" fontWeight={600} color="#8b5cf6">
                {activeOrgsList.length}
              </Typography>
              <Typography variant="h6" fontWeight={400} color="text.secondary">
                / {activeOrgsList.length + inactiveOrgsList.length}
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary">
              Hover for details
            </Typography>
          </Paper>
        </MuiTooltip>

        {/* Active Projects Card */}
        <Paper sx={{ p: 2, minWidth: 110, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant="overline" color="text.secondary">
              Active Projects
            </Typography>
            <MuiTooltip
              title="Total unique projects with activity during the selected period."
              arrow
              placement="top"
              slotProps={{
                tooltip: {
                  sx: {
                    bgcolor: '#1e293b',
                    fontSize: '0.75rem',
                    maxWidth: 280,
                    p: 1.5,
                  },
                },
              }}
            >
              <InfoOutlinedIcon
                sx={{
                  fontSize: 14,
                  color: 'text.secondary',
                  cursor: 'help',
                  '&:hover': { color: 'primary.main' },
                }}
              />
            </MuiTooltip>
          </Box>
          <Typography variant="h4" fontWeight={600} color="#f59e0b">
            {summary?.totalProjects?.toLocaleString() || 0}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Unique Projects
          </Typography>
        </Paper>

        {/* Hours Saved Card */}
        <Paper sx={{ p: 2, minWidth: 110, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant="overline" color="text.secondary">
              Hours Saved
            </Typography>
            <MuiTooltip
              title="Estimated hours saved based on query and workflow usage. Calculation uses company-size-specific multipliers."
              arrow
              placement="top"
              slotProps={{
                tooltip: {
                  sx: {
                    bgcolor: '#1e293b',
                    fontSize: '0.75rem',
                    maxWidth: 280,
                    p: 1.5,
                  },
                },
              }}
            >
              <InfoOutlinedIcon
                sx={{
                  fontSize: 14,
                  color: 'text.secondary',
                  cursor: 'help',
                  '&:hover': { color: 'primary.main' },
                }}
              />
            </MuiTooltip>
          </Box>
          {isHoursSavedLoading ? (
            <Box>
              <Skeleton variant="text" width={100} height={42} />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box
                  component="span"
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    bgcolor: '#10b981',
                    animation: 'pulse 1.5s ease-in-out infinite',
                    '@keyframes pulse': {
                      '0%, 100%': { opacity: 0.4 },
                      '50%': { opacity: 1 },
                    },
                  }}
                />
                Calculating...
              </Typography>
            </Box>
          ) : hoursSavedSummary ? (
            <>
              <Typography variant="h4" fontWeight={600} color="#10b981">
                {hoursSavedSummary.totalHoursSaved.toLocaleString()}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {hoursSavedSummary.totalQueries.toLocaleString()} queries • {hoursSavedSummary.totalWorkflows.toLocaleString()} workflows
              </Typography>
            </>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No data
            </Typography>
          )}
        </Paper>
      </Box>

      {/* Charts */}
      <Grid container spacing={3}>
        {/* DAU Over Time */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="h6">
                  {dauChartView === 'trend' ? 'Daily Active Users Over Time' : 'Daily Active Users by Organization'}
                </Typography>
                <MuiTooltip
                  title={dauChartView === 'trend'
                    ? "Shows unique users and organizations with activity for each day."
                    : "Stacked bar chart showing users per organization each day. Click on any bar to see the list of active users for that day."
                  }
                  arrow
                  placement="top"
                  slotProps={{ tooltip: { sx: { bgcolor: '#1e293b', fontSize: '0.75rem', maxWidth: 280, p: 1.5 } } }}
                >
                  <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help', '&:hover': { color: 'primary.main' } }} />
                </MuiTooltip>
              </Box>
              <ToggleButtonGroup
                value={dauChartView}
                exclusive
                onChange={(_, newView) => newView && setDauChartView(newView as DauChartView)}
                size="small"
              >
                <ToggleButton value="trend" sx={{ px: 1.5 }}>
                  <MuiTooltip title="Trend View">
                    <ShowChartIcon sx={{ fontSize: 18 }} />
                  </MuiTooltip>
                </ToggleButton>
                <ToggleButton value="by-org" sx={{ px: 1.5 }}>
                  <MuiTooltip title="By Organization">
                    <BarChartIcon sx={{ fontSize: 18 }} />
                  </MuiTooltip>
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>

            {dauChartView === 'trend' ? (
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={dauChartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(148, 163, 184, 0.1)" />
                  <XAxis dataKey="dateLabel" stroke="#94a3b8" />
                  <YAxis stroke="#94a3b8" />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#1e293b',
                      border: 'none',
                      borderRadius: 8,
                    }}
                  />
                  <Legend />
                  <Area
                    type="monotone"
                    dataKey="dau"
                    name="Users"
                    stroke="#8b5cf6"
                    fill="#8b5cf680"
                    strokeWidth={2}
                  />
                  <Area
                    type="monotone"
                    dataKey="dao"
                    name="Organizations"
                    stroke="#06b6d4"
                    fill="#06b6d480"
                    strokeWidth={2}
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <Box sx={{ position: 'relative' }}>
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart data={stackedBarData} onClick={handleChartClick}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(148, 163, 184, 0.1)" />
                    <XAxis
                      dataKey="dateLabel"
                      stroke="#94a3b8"
                      angle={-45}
                      textAnchor="end"
                      height={60}
                      tick={{ fontSize: 11 }}
                    />
                    <YAxis stroke="#94a3b8" label={{ value: 'Active Users', angle: -90, position: 'insideLeft', fill: '#94a3b8' }} />
                    <Tooltip
                      content={({ active, payload, label }) => {
                        if (!active || !payload) return null;
                        // Filter out orgs with 0 users on this day
                        const nonZeroPayload = payload.filter((p) => (p.value as number) > 0);
                        if (nonZeroPayload.length === 0) return null;
                        return (
                          <Box
                            sx={{
                              bgcolor: '#1e293b',
                              border: '1px solid rgba(255,255,255,0.1)',
                              borderRadius: 1,
                              p: 1.5,
                              maxWidth: 280,
                            }}
                          >
                            <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block', mb: 1 }}>
                              {label}
                            </Typography>
                            {nonZeroPayload
                              .sort((a, b) => (b.value as number) - (a.value as number))
                              .map((entry) => (
                                <Box key={String(entry.dataKey)} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
                                  <Box sx={{ width: 8, height: 8, borderRadius: '2px', bgcolor: entry.color, flexShrink: 0 }} />
                                  <Typography variant="caption" sx={{ color: '#f8fafc' }}>
                                    {formatOrgDisplayName(entry.name as string)}: {entry.value}
                                  </Typography>
                                </Box>
                              ))}
                          </Box>
                        );
                      }}
                    />
                    {allOrgNames.map((orgName) => (
                      <Bar
                        key={orgName}
                        dataKey={orgName}
                        stackId="users"
                        fill={orgColorMap[orgName]}
                        name={orgName}
                        onClick={handleBarClick}
                        cursor="pointer"
                      />
                    ))}
                  </BarChart>
                </ResponsiveContainer>

                {/* Custom popup for showing user details */}
                {selectedBarData && (
                  <>
                    {/* Backdrop to close popup */}
                    <Box
                      onClick={handleClosePopup}
                      sx={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        zIndex: 1200,
                      }}
                    />
                    {/* Popup content */}
                    <Box
                      sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        zIndex: 1300,
                        bgcolor: '#1e293b',
                        border: '1px solid rgba(255,255,255,0.15)',
                        borderRadius: 2,
                        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
                        p: 2.5,
                        minWidth: 320,
                        maxWidth: 400,
                        maxHeight: 500,
                        display: 'flex',
                        flexDirection: 'column',
                      }}
                    >
                      {/* Header */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                        <Box>
                          <Typography variant="h6" sx={{ color: '#f8fafc', fontSize: '1rem' }}>
                            {format(new Date(selectedBarData.date), 'EEEE, MMM d, yyyy')}
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                            Total: {selectedBarData.totalUsers} active users
                          </Typography>
                        </Box>
                        <Box
                          onClick={handleClosePopup}
                          sx={{
                            cursor: 'pointer',
                            color: '#64748b',
                            fontSize: 24,
                            lineHeight: 1,
                            ml: 2,
                            '&:hover': { color: '#f8fafc' },
                          }}
                        >
                          ×
                        </Box>
                      </Box>

                      {/* Scrollable content */}
                      <Box
                        sx={{
                          overflowY: 'auto',
                          overflowX: 'hidden',
                          flex: 1,
                          pr: 1,
                          '&::-webkit-scrollbar': {
                            width: 6,
                          },
                          '&::-webkit-scrollbar-track': {
                            bgcolor: 'rgba(255,255,255,0.05)',
                            borderRadius: 3,
                          },
                          '&::-webkit-scrollbar-thumb': {
                            bgcolor: 'rgba(255,255,255,0.2)',
                            borderRadius: 3,
                            '&:hover': {
                              bgcolor: 'rgba(255,255,255,0.3)',
                            },
                          },
                        }}
                      >
                        {selectedBarData.orgs.map((org) => (
                          <Box key={org.orgName} sx={{ mb: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                              <Box
                                sx={{
                                  width: 12,
                                  height: 12,
                                  borderRadius: '3px',
                                  bgcolor: org.color,
                                  flexShrink: 0,
                                }}
                              />
                              <Typography variant="body2" sx={{ fontWeight: 600, color: '#f8fafc' }}>
                                {formatOrgDisplayName(org.orgName)} ({org.userCount})
                              </Typography>
                            </Box>
                            {org.userNames.length > 0 && (
                              <Box sx={{ pl: 2.75 }}>
                                {org.userNames.map((name, idx) => (
                                  <Typography key={idx} variant="caption" sx={{ display: 'block', color: '#94a3b8', py: 0.15 }}>
                                    • {name}
                                  </Typography>
                                ))}
                              </Box>
                            )}
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  </>
                )}
              </Box>
            )}

            {/* Legend for stacked bar chart */}
            {dauChartView === 'by-org' && allOrgNames.length > 0 && (
              <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1.5, justifyContent: 'center' }}>
                {allOrgNames.slice(0, 15).map((orgName) => (
                  <Box key={orgName} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: '2px',
                        bgcolor: orgColorMap[orgName],
                      }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {formatOrgDisplayName(orgName)}
                    </Typography>
                  </Box>
                ))}
                {allOrgNames.length > 15 && (
                  <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    +{allOrgNames.length - 15} more
                  </Typography>
                )}
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Left column: WAU on top, Activity by Category below — paired with the tall Top Orgs chart */}
        <Grid item xs={12} md={6}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Typography variant="h6">
                Weekly Active Users Trend (Rolling 7-Day)
              </Typography>
              <MuiTooltip
                title="For each weekday, shows the count of unique users active in the past 7 days. The rolling window includes weekend activity, but data points are only shown for weekdays."
                arrow
                placement="top"
                slotProps={{ tooltip: { sx: { bgcolor: '#1e293b', fontSize: '0.75rem', maxWidth: 280, p: 1.5 } } }}
              >
                <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help', '&:hover': { color: 'primary.main' } }} />
              </MuiTooltip>
            </Box>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={wauChartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(148, 163, 184, 0.1)" />
                <XAxis dataKey="dateLabel" stroke="#94a3b8" />
                <YAxis stroke="#94a3b8" />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#1e293b',
                    border: 'none',
                    borderRadius: 8,
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="wau"
                  name="WAU"
                  stroke="#10b981"
                  strokeWidth={2}
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
            </Paper>

            <Paper sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="h6">
                  Activity by Category
                </Typography>
                <MuiTooltip
                  title="Total event counts grouped by category (e.g., workflow, query, document). Shows what features users engage with most."
                  arrow
                  placement="top"
                  slotProps={{ tooltip: { sx: { bgcolor: '#1e293b', fontSize: '0.75rem', maxWidth: 280, p: 1.5 } } }}
                >
                  <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help', '&:hover': { color: 'primary.main' } }} />
                </MuiTooltip>
              </Box>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={topEventCategories}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(148, 163, 184, 0.1)" />
                  <XAxis dataKey="category" stroke="#94a3b8" angle={-45} textAnchor="end" height={80} />
                  <YAxis stroke="#94a3b8" />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#1e293b',
                      border: 'none',
                      borderRadius: 8,
                    }}
                  />
                  <Bar dataKey="count" name="Events" fill="#06b6d4" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          </Box>
        </Grid>

        {/* Top Organizations */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="h6">
                  Top Organizations by Active Users
                </Typography>
                <MuiTooltip
                  title={orgChartView === 'absolute'
                    ? "Organizations ranked by number of unique users with activity during the selected period."
                    : "Organizations ranked by percentage of their total users that were active during the selected period."
                  }
                  arrow
                  placement="top"
                  slotProps={{ tooltip: { sx: { bgcolor: '#1e293b', fontSize: '0.75rem', maxWidth: 280, p: 1.5 } } }}
                >
                  <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help', '&:hover': { color: 'primary.main' } }} />
                </MuiTooltip>
              </Box>
              <ToggleButtonGroup
                value={orgChartView}
                exclusive
                onChange={(_, newView) => newView && setOrgChartView(newView as OrgChartView)}
                size="small"
              >
                <ToggleButton value="absolute" sx={{ px: 1.5 }}>
                  <MuiTooltip title="Absolute Count">
                    <NumbersIcon sx={{ fontSize: 18 }} />
                  </MuiTooltip>
                </ToggleButton>
                <ToggleButton value="normalized" sx={{ px: 1.5 }}>
                  <MuiTooltip title="Percentage of Org Users">
                    <PercentIcon sx={{ fontSize: 18 }} />
                  </MuiTooltip>
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
            {(() => {
              // Cap to top N so labels stay legible; chart fills the cell height to align with the
              // stacked left column. minHeight keeps it readable when there are few orgs.
              const TOP_N = 25;
              const sourceData = orgChartView === 'absolute'
                ? activityByOrg
                : activityByOrg
                    .map((org) => ({
                      ...org,
                      activePercentage: org.totalOrgUsers > 0
                        ? Math.round((org.uniqueUsers / org.totalOrgUsers) * 100)
                        : 0,
                    }))
                    .sort((a, b) => b.activePercentage - a.activePercentage);
              const chartData = sourceData.slice(0, TOP_N);
              const overflow = Math.max(0, sourceData.length - TOP_N);

              return (
                <>
                  <Box sx={{ flex: 1, minHeight: 320 }}>
                    <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(148, 163, 184, 0.1)" />
                      <XAxis
                        type="number"
                        stroke="#94a3b8"
                        domain={orgChartView === 'normalized' ? [0, 100] : undefined}
                        tickFormatter={orgChartView === 'normalized' ? (value) => `${value}%` : undefined}
                      />
                      <YAxis
                        dataKey="orgName"
                        type="category"
                        width={140}
                        stroke="#94a3b8"
                        tick={{ fontSize: 11 }}
                        interval={0}
                        tickFormatter={(value) => formatOrgDisplayName(value)}
                      />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: '#1e293b',
                          border: 'none',
                          borderRadius: 8,
                        }}
                        formatter={(value, name, item: { payload?: unknown }) => {
                          if (orgChartView === 'normalized') {
                            const org = item.payload as { uniqueUsers?: number; totalOrgUsers?: number };
                            return [`${value}% (${org.uniqueUsers}/${org.totalOrgUsers} users)`, 'Active'];
                          }
                          return [value, name === 'uniqueUsers' ? 'Users' : 'Events'];
                        }}
                      />
                      <Legend />
                      <Bar
                        dataKey={orgChartView === 'absolute' ? 'uniqueUsers' : 'activePercentage'}
                        name={orgChartView === 'absolute' ? 'Unique Users' : 'Active %'}
                        fill={orgChartView === 'absolute' ? '#8b5cf6' : '#10b981'}
                        radius={[0, 4, 4, 0]}
                      />
                    </BarChart>
                    </ResponsiveContainer>
                  </Box>
                  {overflow > 0 && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block', textAlign: 'center', fontStyle: 'italic', mt: 1 }}
                    >
                      Showing top {TOP_N} of {sourceData.length} orgs · see the Organizations tab for the full list
                    </Typography>
                  )}
                </>
              );
            })()}
          </Paper>
        </Grid>

        {/* Event Type Breakdown */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3, maxHeight: 400, overflow: 'auto' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Typography variant="h6">
                Event Type Breakdown
              </Typography>
              <MuiTooltip
                title="Detailed breakdown of activity by category and event type. Shows event count, unique users, and percentage of total activity."
                arrow
                placement="top"
                slotProps={{ tooltip: { sx: { bgcolor: '#1e293b', fontSize: '0.75rem', maxWidth: 280, p: 1.5 } } }}
              >
                <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help', '&:hover': { color: 'primary.main' } }} />
              </MuiTooltip>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {activityByEventType.slice(0, 15).map((item) => (
                <Box
                  key={`${item.eventCategory}-${item.eventType}`}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    p: 1,
                    bgcolor: 'rgba(0,0,0,0.1)',
                    borderRadius: 1,
                  }}
                >
                  <Box>
                    <Typography variant="body2" fontWeight={500}>
                      {item.eventCategory} / {item.eventType}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {item.uniqueUsers} users
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="body2" fontWeight={600}>
                      {item.count.toLocaleString()}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {item.percentage.toFixed(1)}%
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Paper>
        </Grid>
      </Grid>
      </Box>
      </Fade>
      )}

      {/* Generate Report Dialog */}
      <GenerateReportDialog
        open={reportDialogOpen}
        onClose={() => setReportDialogOpen(false)}
        availableOrgs={availableOrgs.filter((org) => !excludedOrgIds.includes(org.id))}
        dateRange={dateRange}
        excludeUserIds={effectiveExcludedUserIds}
      />

      {/* Generate Email Modal */}
      <GenerateEmailModal
        open={emailModalOpen}
        onClose={() => setEmailModalOpen(false)}
        availableOrgs={availableOrgs.filter((org) => !excludedOrgIds.includes(org.id))}
        dateRange={dateRange}
        preselectedOrgId={selectedOrgIds.length === 1 ? selectedOrgIds[0] : undefined}
      />

      {/* Weekly TLDR Modal */}
      <WeeklyTLDRModal
        open={tldrModalOpen}
        onClose={() => setTldrModalOpen(false)}
        availableOrgs={availableOrgs.filter((org) => !excludedOrgIds.includes(org.id))}
        preselectedOrgId={selectedOrgIds.length === 1 ? selectedOrgIds[0] : undefined}
      />
    </Box>
  );
}
